import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSpinner, FaExclamationTriangle, FaSync } from 'react-icons/fa';
import AuthLayout from '../components/AuthLayout';
import { useAuth } from '../context/AuthContext';
import { verifyMagicLink, convertQuotationToAccount, checkEmailExists, regenerateMagicLink } from '../services/quotationAuthService';

export default function QuotationAuthPage() {
  const { magicLink } = useParams();
  const navigate = useNavigate();
  const { user, login, signup, resetPassword } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotationData, setQuotationData] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  // Verificar magic link y email al cargar
  useEffect(() => {
    const init = async () => {
      try {
        // 1. Verificar magic link
        const { isValid, quotation, isConverted, isExpired, error } = await verifyMagicLink(magicLink);
        
        if (!isValid) {
          throw new Error(isExpired ? 'El enlace ha expirado' : error || 'Link inválido');
        }

        if (isConverted) {
          navigate(`/quotation/track/${magicLink}`);
          return;
        }

        // 2. Guardar datos de la cotización
        setQuotationData(quotation);
        const quotationEmail = quotation.get('userEmail');
        setFormData(prev => ({ ...prev, email: quotationEmail }));

        // 3. Verificar si existe el email
        const exists = await checkEmailExists(quotationEmail);
        setUserExists(exists);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [magicLink, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validar que el email coincida con el de la cotización
      if (formData.email !== quotationData.get('userEmail')) {
        throw new Error('No tienes permiso para acceder a esta cotización');
      }

      if (userExists) {
        // Login
        await login(formData.email, formData.password);
      } else {
        // Signup
        await signup(formData);
      }

      // Convertir cotización
      await convertQuotationToAccount(magicLink, formData);
      
      // Redirigir
      navigate(`/quotation/track/${magicLink}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validar que el email coincida con el de la cotización
      if (formData.email !== quotationData.get('userEmail')) {
        throw new Error('No tienes permiso para acceder a esta cotización');
      }

      await resetPassword(formData.email);
      setShowResetPassword(false);
      setError('Se ha enviado un email para restablecer tu contraseña');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Procesando">
        <div className="text-center py-4">
          <FaSpinner className="spin" size={32} />
          <p className="mt-3 mb-0">Verificando...</p>
        </div>
      </AuthLayout>
    );
  }

  if (error) {
    const isExpiredLink = error.includes('expirado');
    
    return (
      <AuthLayout title={isExpiredLink ? "Enlace Expirado" : "Error"}>
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <FaExclamationTriangle />
          {error}
        </div>
        
        {isExpiredLink ? (
          <div className="text-center mt-4">
            <p className="mb-3">Los enlaces de acceso a cotizaciones expiran después de 30 días por razones de seguridad.</p>
            <button 
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={() => navigate('/contact')}
            >
              <FaSync /> Solicitar nuevo enlace
            </button>
          </div>
        ) : (
          <div className="text-center mt-3">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/')}
            >
              Volver al Inicio
            </button>
          </div>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout 
      title={userExists ? 'Iniciar Sesión' : 'Crear Cuenta'}
      subtitle={
        `Esta cotización fue creada con el email ${quotationData.get('userEmail')}. ${
          userExists ? 'Por favor, inicia sesión.' : 'Por favor, completa tu registro.'
        }`
      }
    >
      {/* Mensaje informativo sobre la acción que se realizará */}
      <div className="alert alert-info mb-4" role="alert">
        <small>
          {userExists 
            ? 'Inicia sesión con tu contraseña para acceder a tu cotización y continuar con el proceso.' 
            : 'Crea una cuenta para acceder a tu cotización y realizar un seguimiento del proceso.'}
        </small>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Email (disabled porque viene del magic link) */}
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            value={formData.email}
            disabled
          />
        </div>

        {/* Password */}
        {!showResetPassword && (
          <div className="mb-3">
            <label className="form-label">Contraseña</label>
            <input
              type="password"
              className="form-control"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              required
            />
          </div>
        )}

        {/* Campos adicionales solo para registro */}
        {!userExists && !showResetPassword && (
          <>
            <div className="mb-3">
              <label className="form-label">Nombre</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Teléfono</label>
              <input
                type="tel"
                className="form-control"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </div>
          </>
        )}

        {/* Botones de acción */}
        <div className="auth-buttons">
          {showResetPassword ? (
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResetPassword}
                disabled={loading}
              >
                Enviar Email de Recuperación
              </button>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setShowResetPassword(false)}
              >
                Volver
              </button>
            </>
          ) : (
            <>
              <button
                type="submit"
                className="auth-submit-button"
                disabled={loading}
              >
                {userExists ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </button>
              {userExists && (
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={() => setShowResetPassword(true)}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}
